import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  AtendimentoResumo,
  buscarEventosOficina,
  FatiaDeEvento,
  FiltrosOficina,
  RespostaEventos,
} from '../../../api/dashboardOficinaApi';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import { notify } from '../../../shared/feedback/notify';
import { corDaSerie, formatarNumero, TINTA_GRAFICO } from '../../../shared/ui/chart-palette';
import DetalhePlacasModal from '../../../shared/ui/DetalhePlacasModal';
import {
  AreaEstado,
  CardGrafico,
  EIXO_LINHA,
  EIXO_TICK,
  ESTILO_TOOLTIP,
  Kpi,
  Legenda,
} from '../../../shared/ui/GraficoBase';
import TabelaPaginada, { ColunaTabela } from '../../../shared/ui/TabelaPaginada';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/ui/dashboard-base.css';

/**
 * Aba Eventos (at-133): por que o veículo entrou na oficina e quem deu causa, cruzando cada
 * atendimento com o evento do SGA que o originou.
 *
 * ⚠️ Os LIMITES do espelho ficam escritos na tela, não só no drawer: ele cobre só a base RJ e
 * eventos cadastrados desde 2025. A leitura errada mais provável é "os outros não tiveram evento",
 * e ela é falsa — fora desses limites o sistema simplesmente não sabe.
 *
 * ⚠️ Espelho indisponível (banco do Contac fora) é ERRO, não aba vazia: sem esta distinção a tela
 * diria "nenhum atendimento com evento" sobre uma consulta que não aconteceu.
 */

/**
 * Cor fixa por quem deu causa — a cor segue a entidade, nunca a posição no ranking, senão filtrar
 * repintaria as fatias. "Sem informação" é neutro: é ausência de dado, não um terceiro lado.
 */
const COR_CAUSADOR: Record<string, string> = {
  Associado: corDaSerie(0),
  Terceiro: corDaSerie(1),
  'Roubo ou furto': corDaSerie(2),
  'Sem informação': TINTA_GRAFICO.neutro,
};

const COR_MOTIVO = corDaSerie(0);
const COR_MES = corDaSerie(0);

export const helpSectionsEventos: ComoFuncionaSecao[] = [
  {
    titulo: 'De onde vem o evento',
    conteudo: (
      <>
        <p>
          Cada atendimento é cruzado com o <strong>evento do SGA</strong> que o originou, pelo
          número do evento informado no cadastro do veículo. Quando o atendimento não tem número de
          evento (terceiro cadastrado sem evento), o sistema procura o evento mais recente daquela
          placa até a data de entrada.
        </p>
        <p>
          Os eventos vêm de uma cópia do SGA atualizada uma vez por dia. A data da última
          atualização aparece no topo da aba: se ela estiver antiga, os eventos mais recentes ainda
          não chegaram aqui.
        </p>
      </>
    ),
  },
  {
    titulo: 'O que o sistema não sabe',
    conteudo: (
      <>
        <p>
          A cópia do SGA tem só eventos da <strong>base do Rio de Janeiro</strong>, cadastrados a
          partir de <strong>janeiro de 2025</strong>. Um atendimento fora disso aparece como{' '}
          <strong>sem evento encontrado</strong> — e isso <strong>não</strong> quer dizer que não
          houve evento: quer dizer que o painel não tem como saber.
        </p>
        <p>
          Por isso os percentuais desta aba são calculados sobre os atendimentos{' '}
          <strong>com evento encontrado</strong>, e a quantidade deles aparece escrita no topo.
        </p>
      </>
    ),
  },
  {
    titulo: 'Quem deu causa',
    conteudo: (
      <>
        <p>
          Vem do registro do <strong>associado</strong> no evento: se ele foi o causador, aparece{' '}
          <strong>Associado</strong>; se foi vítima, aparece <strong>Terceiro</strong>. Vale para o
          evento inteiro — inclusive para o veículo do terceiro que esteja na oficina.
        </p>
        <p>
          Quando o associado foi vítima de <strong>roubo ou furto</strong>, não há terceiro que tenha
          dado causa, e o atendimento aparece em <strong>Roubo ou furto</strong>.
        </p>
        <p>
          Quando o evento só tem terceiros registrados, não há de quem afirmar, e o atendimento
          aparece como <strong>Sem informação</strong>. Essa fatia continua no gráfico mesmo vazia:
          a ausência do dado também é informação.
        </p>
      </>
    ),
  },
  {
    titulo: 'Tempo na oficina por motivo',
    conteudo: (
      <p>
        Média de dias na oficina dos atendimentos de cada motivo. Como na aba Região, inclui os
        veículos que <strong>ainda estão</strong> na oficina, contados até hoje — não é a
        permanência final da Visão Geral.
      </p>
    ),
  },
];

interface Props {
  filtros: FiltrosOficina;
  onJanela?: (janela: { dataInicio: string; dataFim: string }) => void;
  sidebarRef?: React.RefObject<HTMLElement>;
}

const dataHoraBr = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const mesBr = (mes: string) => {
  const [ano, m] = mes.split('-');
  return `${m}/${ano}`;
};

const EventosTab: React.FC<Props> = ({ filtros, onJanela, sidebarRef }) => {
  const [dados, setDados] = useState<RespostaEventos | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [detalhe, setDetalhe] = useState<{ titulo: string; placas: string[] } | null>(null);

  const carregar = useCallback(
    async (f: FiltrosOficina) => {
      setCarregando(true);
      try {
        const resposta = await buscarEventosOficina(f);
        if (!resposta || !Array.isArray(resposta.porMotivo) || !resposta.limites) {
          throw new Error('Resposta do servidor em formato inesperado');
        }
        setDados(resposta);
        setErro(null);
        onJanela?.({ dataInicio: resposta.janela.dataInicio, dataFim: resposta.janela.dataFim });
      } catch (e) {
        setDados(null);
        setErro(
          'Não foi possível carregar os eventos. Enquanto este aviso estiver na tela, não há número para ler aqui — recarregue a página; se persistir, acione a TI.',
        );
        notify.errorFrom(
          e,
          'Não foi possível carregar os eventos. Recarregue a página; se persistir, acione a TI.',
          { toastId: 'dashboard-oficina-eventos-falhou' },
        );
      } finally {
        setCarregando(false);
      }
    },
    [onJanela],
  );

  useEffect(() => {
    carregar(filtros);
  }, [filtros, carregar]);

  /*
   * O espelho fora do ar chega como resposta 200 com `espelhoIndisponivel`: é a consulta ao SGA
   * que falhou, não a tela. Tratado como "não respondeu" — nenhum gráfico é desenhado.
   */
  const espelhoFora = dados?.limites.espelhoIndisponivel ?? false;
  const respondeu = dados !== null && !espelhoFora;
  const primeiraCarga = carregando && dados === null;
  const comEvento = respondeu ? dados.cobertura.comEvento : 0;
  const textoVazio = respondeu
    ? 'Nenhum atendimento deste recorte tem evento encontrado no SGA.'
    : 'Não foi possível carregar — veja o aviso acima.';

  const porMotivo = useMemo(() => (respondeu ? dados.porMotivo : []), [respondeu, dados]);
  const porCausador = useMemo(() => (respondeu ? dados.porCausador : []), [respondeu, dados]);
  const porMes = useMemo(
    () => (respondeu ? dados.porMes.map((m) => ({ ...m, rotulo: mesBr(m.mes) })) : []),
    [respondeu, dados],
  );

  const motivoMaisComum = porMotivo[0] ?? null;
  const motivoMaisDemorado = useMemo(
    () =>
      porMotivo.length > 0
        ? porMotivo.reduce((maior, atual) =>
            atual.tempoNaOficinaMediaDias > maior.tempoNaOficinaMediaDias ? atual : maior,
          )
        : null,
    [porMotivo],
  );

  const abrir = (titulo: string, placas: string[]) => setDetalhe({ titulo, placas });

  const COL: Array<ColunaTabela<AtendimentoResumo>> = [
    { titulo: 'Esteira', largura: 180, texto: (i) => i.esteira },
    {
      titulo: 'Dias na oficina',
      largura: 140,
      texto: (i) => (i.naOficina ? `${i.diasNaOficina} dias` : 'Entregue'),
    },
  ];

  const barrasDeFatia = (itens: FatiaDeEvento[], prefixo: string, cor: (f: FatiaDeEvento) => string) => (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={itens.slice(0, 10)} layout="vertical" margin={{ left: 8, right: 16 }}>
        <CartesianGrid stroke={TINTA_GRAFICO.grade} horizontal={false} />
        <XAxis type="number" tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} allowDecimals={false} />
        <YAxis type="category" dataKey="rotulo" tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} width={170} />
        <Tooltip contentStyle={ESTILO_TOOLTIP} formatter={(v: number) => [formatarNumero(v), 'Atendimentos']} />
        <Bar
          dataKey="quantidade"
          radius={[0, 4, 4, 0]}
          cursor="pointer"
          onClick={(evento: any) =>
            evento?.payload?.placas?.length && abrir(`${prefixo}: ${evento.payload.rotulo}`, evento.payload.placas)
          }
        >
          {itens.slice(0, 10).map((f) => (
            <Cell key={f.rotulo} fill={cor(f)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );

  return (
    <div className={carregando && dados !== null ? 'do-atualizando' : undefined}>
      {erro && (
        <div className="erro-de-tela" role="alert">
          {erro}
        </div>
      )}
      {espelhoFora && (
        <div className="erro-de-tela" role="alert">
          Não foi possível consultar os eventos do SGA agora. Nenhum gráfico desta aba é mostrado
          enquanto isso — não é que os atendimentos não tenham evento. Tente novamente em alguns
          minutos; se persistir, acione a TI.
        </div>
      )}

      <div className="do-aba-cabecalho">
        <div className="do-aba-titulo">
          Eventos
          <button type="button" className="help-circle-btn" title="Como Funciona" onClick={() => setIsHelpOpen(true)}>
            ?
          </button>
        </div>
      </div>

      {respondeu && (
        <p className="do-nota-janela">
          Evento encontrado para{' '}
          <strong>
            {formatarNumero(dados.cobertura.comEvento)} de {formatarNumero(dados.cobertura.atendimentos)}
          </strong>{' '}
          atendimentos. A cópia do SGA cobre só a base do Rio de Janeiro e eventos cadastrados desde
          janeiro de 2025: para os demais o sistema não sabe se houve evento.
          {dados.limites.ultimaSincronizacao
            ? ` Eventos atualizados em ${dataHoraBr(dados.limites.ultimaSincronizacao)}.`
            : ' A cópia do SGA ainda não tem data de atualização.'}
        </p>
      )}

      <div className="do-kpis">
        <Kpi
          rotulo="Com evento encontrado"
          valor={respondeu ? formatarNumero(comEvento) : '-'}
          apoio={respondeu ? `De ${formatarNumero(dados.cobertura.atendimentos)} atendimentos` : 'Atendimentos do recorte'}
          carregando={primeiraCarga}
        />
        <Kpi
          rotulo="Motivo mais comum"
          valor={motivoMaisComum ? motivoMaisComum.rotulo : respondeu ? 'Sem evento' : '-'}
          apoio={
            motivoMaisComum
              ? `${formatarNumero(motivoMaisComum.quantidade)} atendimentos (${motivoMaisComum.percentual}%)`
              : 'Entre os atendimentos com evento'
          }
          carregando={primeiraCarga}
          onDetalhar={
            motivoMaisComum
              ? () => abrir(`Motivo: ${motivoMaisComum.rotulo}`, motivoMaisComum.placas)
              : undefined
          }
          detalheRotulo="Ver os veículos"
        />
        <Kpi
          rotulo="Motivo que mais demora"
          valor={motivoMaisDemorado ? motivoMaisDemorado.rotulo : respondeu ? 'Sem evento' : '-'}
          apoio={
            motivoMaisDemorado
              ? `${motivoMaisDemorado.tempoNaOficinaMediaDias} dias na oficina, em média`
              : 'Tempo médio na oficina'
          }
          carregando={primeiraCarga}
          onDetalhar={
            motivoMaisDemorado
              ? () => abrir(`Motivo: ${motivoMaisDemorado.rotulo}`, motivoMaisDemorado.placas)
              : undefined
          }
          detalheRotulo="Ver os veículos"
        />
      </div>

      <div className="do-grid">
        <CardGrafico
          titulo="Atendimentos por motivo do evento"
          descricao="Por que o veículo entrou na oficina. Clique numa barra para ver as placas."
        >
          <div className="do-plot-alto">
            {!respondeu || porMotivo.length === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              barrasDeFatia(porMotivo, 'Motivo', () => COR_MOTIVO)
            )}
          </div>
        </CardGrafico>

        <CardGrafico
          titulo="Quem deu causa"
          descricao="Segundo o registro do associado no evento. Clique numa barra para ver as placas."
        >
          <div className="do-plot-alto">
            {!respondeu || comEvento === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              barrasDeFatia(porCausador, 'Quem deu causa', (f) => COR_CAUSADOR[f.rotulo] ?? TINTA_GRAFICO.neutro)
            )}
          </div>
          {respondeu && comEvento > 0 && (
            <Legenda
              itens={Object.entries(COR_CAUSADOR).map(([rotulo, cor]) => ({ rotulo, cor }))}
            />
          )}
        </CardGrafico>
      </div>

      <div className="do-grid">
        <CardGrafico
          titulo="Motivo e quem deu causa"
          descricao="Quantos atendimentos de cada motivo foram causados pelo associado, pelo terceiro, ou não têm essa informação."
        >
          {!respondeu || dados.motivoPorCausador.length === 0 ? (
            <div className="do-plot">
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            </div>
          ) : (
            <TabelaPaginada
              itens={dados.motivoPorCausador}
              chave={(l) => l.motivo}
              vazio="Nenhum motivo neste recorte."
              colunas={[
                { titulo: 'Motivo', largura: 220, texto: (l) => l.motivo },
                { titulo: 'Associado', largura: 110, texto: (l) => formatarNumero(l.associado) },
                { titulo: 'Terceiro', largura: 110, texto: (l) => formatarNumero(l.terceiro) },
                {
                  titulo: 'Roubo ou furto',
                  largura: 140,
                  texto: (l) => formatarNumero(l.rouboOuFurto ?? 0),
                },
                { titulo: 'Sem informação', largura: 140, texto: (l) => formatarNumero(l.semInformacao) },
              ]}
            />
          )}
        </CardGrafico>

        <CardGrafico
          titulo="Atendimentos com evento por mês de entrada"
          descricao="Mês em que o veículo entrou na oficina, não o mês do evento."
        >
          <div className="do-plot">
            {!respondeu || porMes.length === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={porMes} margin={{ left: 0, right: 16 }}>
                  <CartesianGrid stroke={TINTA_GRAFICO.grade} vertical={false} />
                  <XAxis dataKey="rotulo" tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} />
                  <YAxis tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={ESTILO_TOOLTIP} formatter={(v: number) => [formatarNumero(v), 'Atendimentos']} />
                  <Bar dataKey="quantidade" fill={COR_MES} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardGrafico>
      </div>

      <div className="do-grid-largo">
        <CardGrafico
          titulo="Tempo na oficina por motivo"
          descricao="Média de dias na oficina, contando até hoje quem ainda está lá. Clique numa linha para ver as placas."
        >
          {!respondeu || porMotivo.length === 0 ? (
            <div className="do-plot">
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            </div>
          ) : (
            <TabelaPaginada
              itens={porMotivo}
              chave={(l) => l.rotulo}
              vazio="Nenhum motivo neste recorte."
              onClickLinha={(l) => abrir(`Motivo: ${l.rotulo}`, l.placas)}
              colunas={[
                { titulo: 'Motivo', largura: 220, texto: (l) => l.rotulo },
                { titulo: 'Atendimentos', largura: 120, texto: (l) => formatarNumero(l.quantidade) },
                { titulo: 'Do total com evento', largura: 160, texto: (l) => `${l.percentual}%` },
                { titulo: 'Tempo na oficina (média)', largura: 190, texto: (l) => `${l.tempoNaOficinaMediaDias} dias` },
              ]}
            />
          )}
        </CardGrafico>
      </div>

      {detalhe && dados && (
        <DetalhePlacasModal
          titulo={detalhe.titulo}
          placas={detalhe.placas}
          colunas={COL}
          atendimentos={dados.atendimentos}
          onFechar={() => setDetalhe(null)}
          sidebarRef={sidebarRef}
        />
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Eventos"
        secoes={helpSectionsEventos}
      />
    </div>
  );
};

export default EventosTab;
